import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { RefreshCw, CircleDot, AlertTriangle, ShieldAlert } from "lucide-react";
import { StatCard, StatusBadge, fmtDateTime } from "@/components/portal/parts";
import { ReservationListCard, ReservationDetailDrawer } from "@/components/portal/ReservationDetail";
import { DateField } from "@/components/portal/DateField";

const TABS = [["operations", "Operations Board"], ["viewas", "View as Client"], ["health", "Integration Health"], ["clients", "Client Accounts"], ["accounts", "Corporate Accounts"], ["mappings", "Account Mappings"], ["queue", "Review Queue"], ["sync", "Sync & Audit"]];
const box = "rounded-2xl border border-gold/15 bg-obsidian-800 p-5";
const inp = "rounded-lg border border-gold/20 bg-obsidian-900 px-3 py-2 text-xs text-ivory outline-none focus:border-gold/60";

export default function PortalAdmin({ tab: extTab, onTab }) {
  const [localTab, setLocalTab] = useState("operations");
  const controlled = typeof onTab === "function";
  const tab = controlled ? extTab : localTab;
  const setTab = controlled ? onTab : setLocalTab;
  return (
    <section className={controlled ? "" : "mt-10"} data-testid="portal-admin">
      {!controlled && <>
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Client Portal · Administration</p>
        <h2 className="mt-2 font-display text-3xl font-semibold text-ivory">Corporate & Reservation Controls</h2>
        <p className="mt-2 max-w-2xl text-sm text-ivory/55">Manage website accounts and account-to-reservation mappings. Reservation data itself is read-only and owned by Limo Anywhere.</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`portal-admin-tab-${k}`} className={`rounded-full px-4 py-2 text-xs font-semibold ${tab === k ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`}>{l}</button>
          ))}
        </div>
      </>}
      <div className={controlled ? "" : "mt-5"}>
        {tab === "operations" && <OperationsBoard />}
        {tab === "viewas" && <ViewAsClient />}
        {tab === "health" && <Health />}
        {tab === "clients" && <ClientAccounts />}
        {tab === "accounts" && <Accounts />}
        {tab === "mappings" && <Mappings />}
        {tab === "queue" && <Queue />}
        {tab === "sync" && <SyncAudit />}
      </div>
    </section>
  );
}

const HEALTH = {
  connected: ["Connected", "text-gold-light border-gold/40 bg-gold/10"],
  active: ["Active", "text-gold-light border-gold/40 bg-gold/10"],
  verified_active: ["Verified · Active", "text-gold-light border-gold/40 bg-gold/10"],
  owner_confirmed: ["Owner Confirmed", "text-amber-400 border-amber-500/40 bg-amber-500/10"],
  configured: ["Configured", "text-amber-400 border-amber-500/40 bg-amber-500/10"],
  test_mode: ["Configured · Test Mode", "text-amber-400 border-amber-500/40 bg-amber-500/10"],
  delayed: ["Delayed", "text-orange-400 border-orange-500/40 bg-orange-500/10"],
  disconnected: ["Disconnected", "text-red-400 border-red-500/40 bg-red-500/10"],
  not_configured: ["Not Configured", "text-red-400 border-red-500/40 bg-red-500/10"],
  not_activated: ["Not Activated", "text-ivory/60 border-ivory/25 bg-ivory/5"],
  inactive: ["Inactive", "text-ivory/60 border-ivory/25 bg-ivory/5"],
  unverified: ["Unverified", "text-ivory/60 border-ivory/25 bg-ivory/5"],
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function Badge({ state, testId }) {
  const [label, cls] = HEALTH[state] || [state, "text-ivory/60 border-ivory/25 bg-ivory/5"];
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${cls}`}>
      <CircleDot className="h-3 w-3" /> {label}
    </span>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-gold/10 bg-obsidian-900 px-4 py-3">
      <p className="text-[10px] uppercase tracking-wider text-ivory/40">{label}</p>
      <p className="mt-1 text-lg font-semibold text-ivory" data-testid={testId}>{value}</p>
    </div>
  );
}

function Card({ title, state, testIdPrefix, children }) {
  return (
    <div className={box} data-testid={`health-card-${testIdPrefix}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">{title}</p>
        {state && <Badge state={state} testId={`health-${testIdPrefix}-state`} />}
      </div>
      <div className="mt-3 text-sm text-ivory/75">{children}</div>
    </div>
  );
}

function ClientAccounts() {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_at");
  const [data, setData] = useState({ accounts: [], metrics: null });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/portal/client-accounts?status=${status}&q=${encodeURIComponent(q)}&sort=${sort}&order=desc`);
      setData(data);
    } catch { toast.error("Could not load client accounts"); }
    finally { setLoading(false); }
  }, [status, q, sort]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async (userId, action, verb) => {
    if (action === "DELETE") {
      if (!window.confirm("Delete this client account? A recoverable backup is kept.")) return;
      try { await api.delete(`/admin/portal/client-accounts/${userId}`); toast.success("Account deleted (backup saved)"); load(); }
      catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
      return;
    }
    try { await api.post(`/admin/portal/client-accounts/${userId}/${action}`); toast.success(verb); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const m = data.metrics;
  const METRICS = m ? [
    ["Total Registered", m.total, "metric-total"],
    ["Active", m.active, "metric-active"],
    ["Pending Verification", m.pending_verification, "metric-pending-verification"],
    ["Suspended", m.suspended, "metric-suspended"],
    ["New Today", m.new_today, "metric-new-today"],
    ["New This Week", m.new_this_week, "metric-new-week"],
    ["Recently Active (30d)", m.recently_active, "metric-recent"],
  ] : [];

  return (
    <div className="space-y-4" data-testid="client-accounts">
      <p className="text-xs text-ivory/50">Individual signups are auto-activated — you monitor accounts here (no approval needed). You can suspend, reactivate, revoke sessions, or delete an account.</p>

      {m && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="client-metrics">
          {METRICS.map(([label, value, tid]) => <Stat key={tid} label={label} value={value} testId={tid} />)}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {["all", "active", "suspended", "unverified"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`client-filter-${s}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize ${status === s ? "bg-gold text-obsidian-900" : "border border-gold/25 text-ivory/70"}`}>{s}</button>
          ))}
          <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email…" className={`${inp} w-56`} />
          <select data-testid="client-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={inp}>
            <option value="created_at">Sort: Registration date</option>
            <option value="last_login">Sort: Last login</option>
            <option value="last_active">Sort: Last activity</option>
            <option value="login_count">Sort: Login count</option>
            <option value="name">Sort: Name</option>
          </select>
        </div>
        <button onClick={load} data-testid="clients-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 px-3 py-1.5 text-xs text-ivory/80 hover:bg-gold/10"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gold/15 bg-obsidian-800" data-testid="client-table">
        <table className="w-full min-w-[840px] text-left text-sm">
          <thead>
            <tr className="border-b border-gold/10 text-[10px] font-mono uppercase tracking-wider text-ivory/40">
              <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Method</th><th className="px-4 py-3">Verified</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">Registered</th><th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3">Logins</th><th className="px-4 py-3 text-right">Manage</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-ivory/50">Loading…</td></tr>}
            {!loading && data.accounts.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-ivory/50" data-testid="clients-empty">No accounts match.</td></tr>}
            {!loading && data.accounts.map((u) => (
              <tr key={u.user_id} className="border-b border-gold/5" data-testid={`client-row-${u.user_id}`}>
                <td className="px-4 py-3"><span className="font-medium text-ivory">{u.name || "—"}</span><br /><span className="text-xs text-ivory/45">{u.email}</span></td>
                <td className="px-4 py-3 text-xs capitalize text-ivory/70">{u.registration_method}</td>
                <td className="px-4 py-3">{u.email_verified ? <span className="text-gold-light">Yes</span> : <span className="text-amber-400">No</span>}</td>
                <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${u.account_status === "suspended" ? "border-orange-500/40 bg-orange-500/10 text-orange-400" : "border-gold/40 bg-gold/10 text-gold-light"}`}>{u.account_status}</span></td>
                <td className="px-4 py-3 text-xs text-ivory/60">{fmt(u.created_at)}</td>
                <td className="px-4 py-3 text-xs text-ivory/60">{fmt(u.last_login)}</td>
                <td className="px-4 py-3 text-xs text-ivory/70">{u.login_count}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    {u.account_status === "suspended"
                      ? <button onClick={() => act(u.user_id, "reactivate", "Reactivated")} data-testid={`reactivate-${u.user_id}`} className="rounded-full border border-gold/40 px-3 py-1 text-xs text-gold-light hover:bg-gold/10">Reactivate</button>
                      : <button onClick={() => act(u.user_id, "suspend", "Suspended")} data-testid={`suspend-${u.user_id}`} className="rounded-full border border-orange-500/40 px-3 py-1 text-xs text-orange-400 hover:bg-orange-500/10">Suspend</button>}
                    <button onClick={() => act(u.user_id, "revoke-sessions", "Sessions revoked")} data-testid={`revoke-${u.user_id}`} className="rounded-full border border-gold/25 px-3 py-1 text-xs text-ivory/70 hover:bg-gold/10">Revoke sessions</button>
                    <button onClick={() => act(u.user_id, "DELETE", "")} data-testid={`delete-${u.user_id}`} className="rounded-full border border-red-500/30 px-3 py-1 text-xs text-red-400/80 hover:bg-red-500/10">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StaffAccounts />
    </div>
  );
}

function StaffAccounts() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/portal/staff-accounts"); setUsers(data.users || []); }
    catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (u) => {
    if (!window.confirm(`Remove sign-in record for ${u.email}? A recoverable backup is kept.`)) return;
    try { await api.delete(`/admin/portal/staff-accounts/${u.user_id}`); toast.success("Sign-in record removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (loading || users.length === 0) return null;
  return (
    <div className="mt-6" data-testid="staff-accounts">
      <p className="mb-2 font-mono text-[11px] uppercase tracking-widest text-ivory/45">Staff & OAuth sign-in records</p>
      <div className="overflow-hidden rounded-2xl border border-gold/15 bg-obsidian-800">
        {users.map((u) => (
          <div key={u.user_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-gold/5 px-4 py-3 last:border-0" data-testid={`staff-row-${u.user_id}`}>
            <div>
              <span className="text-sm text-ivory">{u.name || "—"}</span> <span className="text-xs text-ivory/45">{u.email}</span>
              {u.is_admin && <span className="ml-2 rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[10px] font-semibold text-gold">ADMIN</span>}
            </div>
            {u.is_admin
              ? <span className="text-[11px] text-ivory/35">Protected (in ADMIN_EMAILS)</span>
              : <button onClick={() => del(u)} data-testid={`staff-delete-${u.user_id}`} className="rounded-full border border-red-500/30 px-3 py-1 text-xs text-red-400/80 hover:bg-red-500/10">Remove</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Health() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/portal/integration-health"); setH(data); }
    catch { toast.error("Could not load integration health"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !h) return <div className={box} data-testid="health-loading"><p className="text-sm text-ivory/50">Loading live integration status…</p></div>;
  if (!h) return null;

  const la = h.limo_anywhere, r = h.reservations, tm = h.transmedics_30000, em = h.email, gw = h.google_workspace, cl = h.cleanup;

  return (
    <div className="space-y-4" data-testid="integration-health">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-ivory/50">Live status from actual backend data · generated {fmt(h.generated_at)}{h.environment?.seed_mode ? " · PREVIEW (seed mode)" : h.environment?.is_production ? " · PRODUCTION" : ""}</p>
        <div className="flex items-center gap-2">
          {h.clients && (
            <span data-testid="health-client-count" className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-gold/10 px-3 py-1.5 text-xs font-semibold text-gold">
              {h.clients.total} client{h.clients.total === 1 ? "" : "s"} · {h.clients.active} active
            </span>
          )}
          {h.portal_live === false && (
            <span data-testid="health-portal-comingsoon" className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300">
              Portal: Coming Soon
            </span>
          )}
          <button onClick={load} data-testid="health-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 px-3 py-1.5 text-xs text-ivory/80 hover:bg-gold/10"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        </div>
      </div>

      {h.alerts?.length > 0 && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5" data-testid="health-alerts">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-red-400"><ShieldAlert className="h-4 w-4" /> Critical Integration Errors & Alerts</p>
          <ul className="mt-3 space-y-1.5">
            {h.alerts.map((a, i) => (
              <li key={i} className={`flex items-start gap-2 text-sm ${a.level === "critical" ? "text-red-300" : "text-amber-300"}`} data-testid={`health-alert-${i}`}>
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {a.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {h.alerts?.length === 0 && (
        <p className="rounded-2xl border border-gold/25 bg-gold/5 px-5 py-3 text-sm text-gold-light" data-testid="health-no-alerts">No critical integration errors. All monitored services are nominal.</p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Limo Anywhere · Zapier" state={la.state} testIdPrefix="limo">
          <p className="text-xs text-ivory/55">One-way ingest (read-only). Webhook secret {la.zapier_secret_configured ? "configured" : "missing"}.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Stat label="Last successful sync" value={fmt(la.last_success_at)} testId="health-last-sync" />
            <Stat label="Last attempt" value={fmt(la.last_attempt_at)} />
          </div>
          {la.last_error && <p className="mt-2 text-xs text-red-400">Last error: {la.last_error}</p>}
          {la.state === "not_activated" && <p className="mt-2 text-xs text-ivory/50">No synchronization has run yet — activate the Zap after the controlled test.</p>}
        </Card>

        <Card title="Reservation Synchronization" testIdPrefix="reservations">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <Stat label="Synchronized" value={r.synchronized_total} testId="health-total" />
            <Stat label="Visible" value={r.matched_visible ?? r.matched} testId="health-visible" />
            <Stat label="Offers pending" value={r.offers_pending ?? 0} testId="health-offers-pending" />
            <Stat label="Unmatched" value={r.unmatched} testId="health-unmatched" />
            <Stat label="Failed webhooks" value={r.failed_webhook_events} testId="health-failed-webhooks" />
          </div>
          <p className="mt-2 text-[11px] text-ivory/45">Unaccepted LA Net / eFarm-in offers are counted under "Offers pending" and stay hidden from corporate dashboards until you accept them in Limo Anywhere.</p>
          <div className="mt-3 rounded-xl border border-gold/10 bg-obsidian-900 px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-ivory/40">Last received reservation</p>
            {r.last_received ? (
              <p className="mt-1 text-sm text-ivory" data-testid="health-last-received">
                <span className="font-mono text-gold">{r.last_received.confirmation_number}</span> · {r.last_received.status} · {r.last_received.trip_type} <span className="text-ivory/45">({fmt(r.last_received.last_synced_at)})</span>
              </p>
            ) : <p className="mt-1 text-sm text-ivory/50" data-testid="health-last-received">None received yet</p>}
          </div>
        </Card>

        <Card title="TransMedics · Booking Account #30000" state={tm.state} testIdPrefix="transmedics">
          {tm.state === "not_configured"
            ? <p className="text-xs text-ivory/55">No production mapping for account 30000 yet — it stays inactive until a real Zapier payload is validated and you approve it.</p>
            : <p className="text-xs text-ivory/55">Mapped to <strong className="text-ivory">{tm.company || "—"}</strong> · expected trip type <strong className="text-ivory">{tm.expected_trip_type || "any"}</strong>. Owner confirmed: {tm.owner_confirmed ? "yes" : "no"} · Integration verified: {tm.verified ? "yes" : "no"}.</p>}
        </Card>

        <Card title="Resend · Transactional Email" state={em.state} testIdPrefix="email">
          <p className="text-xs text-ivory/55">{em.test_mode ? "Test mode suppresses all real sends (no customer email)." : "Live delivery enabled via the managed email proxy."} Domain/sender is managed by the platform.</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Stat label="Sent" value={em.sent} />
            <Stat label="Failed" value={em.failed} />
            <Stat label="Suppressed (test)" value={em.suppressed} />
          </div>
        </Card>

        <Card title="Google Workspace" state={gw.state} testIdPrefix="google">
          {gw.connected
            ? <p className="text-xs text-ivory/55">Connected as <strong className="text-ivory">{gw.account}</strong> since {fmt(gw.connected_at)}.</p>
            : <p className="text-xs text-ivory/55">{gw.configured ? "Configured but not connected — reconnect from the Workspace card above." : "Not configured."}</p>}
        </Card>

        <Card title="Synthetic-Data Cleanup" state={cl.verified ? (cl.total_purged ? "connected" : "connected") : "not_activated"} testIdPrefix="cleanup">
          {cl.verified
            ? <p className="text-xs text-ivory/55" data-testid="health-cleanup">Verified clean on last startup ({fmt(cl.at)}). Records purged this run: <strong className="text-ivory">{cl.total_purged}</strong>{cl.total_purged === 0 ? " (already clean)" : ""}.</p>
            : <p className="text-xs text-ivory/55" data-testid="health-cleanup">Not run in this environment (preview seed mode keeps demo data).</p>}
        </Card>
      </div>
    </div>
  );
}

function useCompanies(status = "all") {  const [companies, setCompanies] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/portal/corporate-accounts?status=${status}`);
    setCompanies(data.companies);
  }, [status]);
  useEffect(() => { load(); }, [load]);
  return [companies, load];
}

function Accounts() {
  const [companies, load] = useCompanies("all");
  const [primaryEmail, setPrimaryEmail] = useState({});
  const [newName, setNewName] = useState("");
  const act = async (id, action) => {
    try { await api.post(`/admin/portal/corporate-accounts/${id}/${action}`); toast.success(`Company ${action}d`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const deactivate = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}? This revokes ALL its memberships, invitations and mappings and signs out its users immediately. A recoverable backup is saved; reservation history is preserved. Reactivating later will require new invitations.`)) return;
    try { const { data } = await api.post(`/admin/portal/corporate-accounts/${id}/deactivate`); toast.success(data.already ? "Already deactivated" : `Deactivated · ${data.members_revoked} memberships & ${data.invitations_revoked} invitations revoked, ${data.sessions_invalidated} session(s) invalidated`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Deactivation failed"); }
  };
  const reactivate = async (id) => {
    try { const { data } = await api.post(`/admin/portal/corporate-accounts/${id}/reactivate`); toast.success(data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reactivation failed"); }
  };
  const toggleVisibility = async (id, visible) => {
    try { const { data } = await api.post(`/admin/portal/corporate-accounts/${id}/reservation-visibility`, { visible }); toast.success(data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update visibility"); }
  };
  const createCompany = async () => {
    const name = newName.trim();
    if (name.length < 2) { toast.error("Enter a company name"); return; }
    try { await api.post(`/admin/portal/corporate-accounts`, { name }); toast.success(`Company "${name}" created`); setNewName(""); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not create company"); }
  };
  const appointPrimary = async (id) => {
    const email = (primaryEmail[id] || "").trim();
    if (!email) { toast.error("Enter the administrator's email"); return; }
    try { await api.post(`/admin/portal/corporate-accounts/${id}/primary-admin`, { email }); toast.success("Primary Administrator invitation sent"); setPrimaryEmail({ ...primaryEmail, [id]: "" }); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <div className={box} data-testid="admin-accounts">
      <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-gold/10 pb-4">
        <span className="text-[10px] uppercase tracking-wider text-ivory/40">Create corporate company:</span>
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. TransMedics" className={inp} data-testid="new-company-name" />
        <button onClick={createCompany} data-testid="create-company-btn" className="rounded-full bg-gold px-4 py-2 text-xs font-semibold text-obsidian-900 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]">Add Company</button>
        <span className="text-[10px] text-ivory/35">No invitation is sent; no primary admin assigned.</span>
      </div>
      {companies.length === 0 && <p className="text-sm text-ivory/50">No corporate accounts yet.</p>}
      {companies.map((c) => (
        <div key={c.company_id} className="border-b border-gold/5 py-3" data-testid={`company-row-${c.company_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm text-ivory">{c.name} <span className={`ml-2 text-xs ${c.status === "approved" ? "text-gold-light" : c.status === "pending" ? "text-amber-400" : c.status === "suspended" ? "text-orange-400" : "text-red-400"}`}>{c.status}</span></p>
              <p className="text-xs text-ivory/45">{c.requester?.email || "—"} · {c.member_count} members · {c.mapping_count} mappings</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {c.status === "pending" && (
                <>
                  <button onClick={() => act(c.company_id, "approve")} data-testid={`approve-${c.company_id}`} className="rounded-full bg-gold px-4 py-2 text-xs font-semibold text-obsidian-900">Approve</button>
                  <button onClick={() => act(c.company_id, "reject")} className="rounded-full border border-red-500/40 px-4 py-2 text-xs text-red-400">Reject</button>
                </>
              )}
              {c.status === "approved" && <button onClick={() => act(c.company_id, "suspend")} data-testid={`suspend-${c.company_id}`} className="rounded-full border border-orange-500/40 px-4 py-2 text-xs text-orange-400 hover:bg-orange-500/10">Suspend</button>}
              {c.status === "suspended" && <button onClick={() => act(c.company_id, "unsuspend")} data-testid={`unsuspend-${c.company_id}`} className="rounded-full border border-gold/40 px-4 py-2 text-xs text-gold-light hover:bg-gold/10">Reactivate</button>}
              {(c.status === "approved" || c.status === "suspended") && <button onClick={() => deactivate(c.company_id, c.name)} data-testid={`deactivate-${c.company_id}`} className="rounded-full border border-red-500/40 px-4 py-2 text-xs text-red-400 hover:bg-red-500/10">Deactivate</button>}
              {c.status === "approved" && <button onClick={() => toggleVisibility(c.company_id, !c.reservations_visible)} data-testid={`visibility-${c.company_id}`} className={`rounded-full border px-4 py-2 text-xs hover:bg-gold/10 ${c.reservations_visible ? "border-gold/40 text-gold-light" : "border-gold/25 text-ivory/70"}`}>{c.reservations_visible ? "Board: Live for dispatchers ✓" : "Board: Hidden"}</button>}
              {c.status === "deactivated" && <button onClick={() => reactivate(c.company_id)} data-testid={`reactivate-${c.company_id}`} className="rounded-full border border-gold/40 px-4 py-2 text-xs text-gold-light hover:bg-gold/10">Reactivate (new invites required)</button>}
            </div>
          </div>
          {(c.status === "approved" || c.status === "suspended") && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gold/10 pt-3">
              <span className="text-[10px] uppercase tracking-wider text-ivory/40">Appoint / replace Primary Admin:</span>
              <input value={primaryEmail[c.company_id] || ""} onChange={(e) => setPrimaryEmail({ ...primaryEmail, [c.company_id]: e.target.value })} placeholder="admin@company.com" className={inp} data-testid={`primary-email-${c.company_id}`} />
              <button onClick={() => appointPrimary(c.company_id)} data-testid={`appoint-primary-${c.company_id}`} className="rounded-full border border-gold/25 px-4 py-2 text-xs text-ivory/80 hover:bg-gold/10">Send Invitation</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const ID_TYPES = [
  ["booking_account_number", "Booking Account #"],
  ["affiliate_reference", "Affiliate Reference # (LA Net)"],
  ["po_reference", "PO Reference # (GroundXchange)"],
  ["passenger_account_number", "Passenger Account #"],
];
const idTypeLabel = (t) => (ID_TYPES.find(([k]) => k === t) || [t, t])[1];

function Mappings() {
  const [companies] = useCompanies("approved");
  const [mappings, setMappings] = useState([]);
  const [form, setForm] = useState({ company_id: "", source_identifier: "", identifier_type: "booking_account_number", label: "", expected_trip_type: "", owner_confirmed: false });
  const load = useCallback(async () => { const { data } = await api.get("/admin/portal/mappings"); setMappings(data.mappings); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    const comp = companies.find((c) => c.company_id === form.company_id);
    const payload = { ...form, expected_trip_type: form.expected_trip_type || null,
      expected_company_aliases: comp ? [comp.name] : [] };
    try { const { data } = await api.post("/admin/portal/mappings", payload); toast.success(`Mapped — ${data.reservations_matched} reservations matched`); setForm({ company_id: "", source_identifier: "", identifier_type: "booking_account_number", label: "", expected_trip_type: "", owner_confirmed: false }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/portal/mappings/${id}`); toast.success("Mapping removed"); load(); } catch { toast.error("Failed"); } };
  const verify = async (id) => { try { await api.post(`/admin/portal/mappings/${id}/verify`); toast.success("Marked Owner Confirmed / Verified"); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="space-y-4" data-testid="admin-mappings">
      <div className={box}>
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Add Mapping</p>
        <p className="mt-1 text-xs text-ivory/50">Map a <strong>verified</strong> Limo Anywhere source identifier to a corporate company. Choose which field to authorize on — company name alone is never accepted. An optional expected Trip Type adds a safeguard: trips with contradicting source info go to the review queue instead of auto-associating.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <select data-testid="mapping-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} className={inp}>
            <option value="">Select company…</option>
            {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
          <select data-testid="mapping-id-type" value={form.identifier_type} onChange={(e) => setForm({ ...form, identifier_type: e.target.value })} className={inp}>
            {ID_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input data-testid="mapping-identifier" placeholder="Identifier value (e.g. 30000)" value={form.source_identifier} onChange={(e) => setForm({ ...form, source_identifier: e.target.value })} className={inp} />
          <select data-testid="mapping-trip-type" value={form.expected_trip_type} onChange={(e) => setForm({ ...form, expected_trip_type: e.target.value })} className={inp}>
            <option value="">Expected trip type (optional)</option>
            <option value="farm_in">Farm In</option>
            <option value="farm_out">Farm Out</option>
            <option value="in_house">In House</option>
          </select>
          <input data-testid="mapping-label" placeholder="Label (optional)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={inp} />
          <label className="flex items-center gap-1.5 text-xs text-ivory/70"><input type="checkbox" data-testid="mapping-owner-confirmed" checked={form.owner_confirmed} onChange={(e) => setForm({ ...form, owner_confirmed: e.target.checked })} /> Owner confirmed</label>
          <button data-testid="mapping-create" onClick={create} disabled={!form.company_id || !form.source_identifier} className="rounded-full bg-gold px-5 py-2 text-xs font-semibold text-obsidian-900 disabled:opacity-40">Create</button>
        </div>
      </div>
      <div className={box}>
        {mappings.length === 0 && <p className="text-sm text-ivory/50">No mappings yet.</p>}
        {mappings.filter((m) => m.active).map((m) => (
          <div key={m.mapping_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-gold/5 py-3 text-sm" data-testid={`mapping-row-${m.source_identifier}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-gold/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-gold/80">{idTypeLabel(m.identifier_type)}</span>
              <span className="font-mono text-gold">{m.source_identifier}</span>
              <span className="text-ivory/50">→ {m.company_name}</span>
              {m.expected_trip_type && <span className="text-[10px] uppercase tracking-wider text-ivory/40">expects {m.expected_trip_type}</span>}
              {m.label && <span className="text-ivory/40"> · {m.label}</span>}
              {m.verified
                ? <span data-testid={`mapping-verified-${m.source_identifier}`} className="rounded-full border border-gold/40 px-2 py-0.5 text-[10px] font-semibold text-gold-light">Owner Confirmed · Verified</span>
                : m.owner_confirmed
                  ? <span className="rounded-full border border-amber-500/40 px-2 py-0.5 text-[10px] text-amber-400">Owner confirmed · pending validation</span>
                  : <span className="rounded-full border border-ivory/20 px-2 py-0.5 text-[10px] text-ivory/50">unverified</span>}
            </div>
            <div className="flex gap-3">
              {!m.verified && <button onClick={() => verify(m.mapping_id)} data-testid={`mapping-verify-${m.source_identifier}`} className="text-xs text-gold-light hover:underline">Mark Verified</button>}
              <button onClick={() => remove(m.mapping_id)} className="text-xs text-red-400 hover:underline">Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Queue() {
  const [companies] = useCompanies("approved");
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState({});
  const load = useCallback(async () => { const { data } = await api.get("/admin/portal/review-queue"); setRows(data.reservations); }, []);
  useEffect(() => { load(); }, [load]);
  const assign = async (rid) => {
    const company_id = pick[rid];
    if (!company_id) { toast.error("Choose a company"); return; }
    try { await api.post(`/admin/portal/review-queue/${rid}/assign`, { company_id }); toast.success("Assigned"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <div className={box} data-testid="admin-queue">
      <p className="text-xs text-ivory/50">Reservations whose corporate ownership could not be verified automatically. Not visible to any client until assigned.</p>
      {rows.length === 0 && <p className="mt-4 text-sm text-ivory/50">Review queue is empty.</p>}
      {rows.map((r) => (
        <div key={r.reservation_id} className="flex flex-wrap items-center justify-between gap-3 border-b border-gold/5 py-3" data-testid={`queue-row-${r.confirmation_number}`}>
          <div className="min-w-0">
            <p className="text-sm text-ivory"><span className="font-mono text-gold">{r.confirmation_number}</span> · {r.passenger_name} <span className="ml-1 text-[10px] uppercase tracking-wider text-ivory/40">{r.trip_type}</span></p>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ivory/45">
              <span>Booking Acct: <span className="font-mono text-ivory/70">{r.booking_account_number || "—"}</span></span>
              <span>Affiliate Ref: <span className="font-mono text-ivory/70">{r.affiliate_reference || "—"}</span></span>
              <span>PO Ref: <span className="font-mono text-ivory/70">{r.po_reference || "—"}</span></span>
              <span>Passenger Acct: <span className="font-mono text-ivory/70">{r.passenger_account_number || "—"}</span></span>
              {r.company_name && <span className="text-amber-400/80">Claims: “{r.company_name}” (unverified — not used for matching)</span>}
            </div>
            {r.conflict_reason && <p className="mt-0.5 text-xs font-semibold text-red-400" data-testid={`queue-conflict-${r.confirmation_number}`}>⚠ Source conflict: {r.conflict_reason}</p>}
            <p className="mt-0.5 text-xs text-ivory/40">{r.pickup_address} → {r.dropoff_address}</p>
          </div>
          <div className="flex gap-2">
            <select value={pick[r.reservation_id] || ""} onChange={(e) => setPick({ ...pick, [r.reservation_id]: e.target.value })} className={inp} data-testid={`queue-company-${r.confirmation_number}`}>
              <option value="">Assign to…</option>
              {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </select>
            <button onClick={() => assign(r.reservation_id)} data-testid={`queue-assign-${r.confirmation_number}`} className="rounded-full bg-gold px-4 py-2 text-xs font-semibold text-obsidian-900">Assign</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SyncAudit() {
  const [sync, setSync] = useState(null);
  const [audit, setAudit] = useState([]);
  const [emails, setEmails] = useState([]);
  const loadSync = () => api.get("/admin/portal/sync").then(({ data }) => setSync(data)).catch(() => {});
  useEffect(() => {
    loadSync();
    api.get("/admin/portal/audit?limit=40").then(({ data }) => setAudit(data.events)).catch(() => {});
    api.get("/admin/portal/emails?limit=40").then(({ data }) => setEmails(data.emails)).catch(() => {});
  }, []);
  const retry = async () => { try { await api.post("/admin/portal/sync/retry"); toast.success("Sync retried (no duplicates)"); loadSync(); } catch { toast.error("Retry failed"); } };
  const simulate = async (state) => { try { await api.post(`/admin/portal/sync/simulate?state=${state}`); toast.success(`Simulated ${state}`); loadSync(); } catch { toast.error("Not allowed"); } };
  return (
    <div className="space-y-4" data-testid="admin-sync">
      <div className={box}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Synchronization</p>
          <div className="flex gap-2">
            <button onClick={retry} data-testid="sync-retry" className="rounded-full border border-gold/25 px-3 py-1.5 text-xs text-ivory/80 hover:bg-gold/10">Retry sync</button>
          </div>
        </div>
        {sync ? (
          <div className="mt-3 text-sm text-ivory/80">
            <p>Reservations: {sync.counts.total} · Matched: {sync.counts.matched} · In review: {sync.counts.unmatched}</p>
            {sync.sources.map((s) => (
              <p key={s.source} className="mt-1 text-xs text-ivory/55">{s.source}: last success {s.last_success_at ? new Date(s.last_success_at).toLocaleString() : "never"}{s.last_error ? ` · error: ${s.last_error}` : ""}</p>
            ))}
            {sync.sources.length === 0 && <p className="mt-1 text-xs text-amber-400">No synchronization has run yet (connect Zapier to start).</p>}
          </div>
        ) : <p className="mt-3 text-sm text-ivory/50">Loading…</p>}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gold/10 pt-3">
          <span className="text-[10px] uppercase tracking-wider text-ivory/40">Demo health state:</span>
          {["connected", "delayed", "disconnected"].map((st) => (
            <button key={st} onClick={() => simulate(st)} data-testid={`sim-${st}`} className="rounded-full border border-gold/20 px-3 py-1 text-xs text-ivory/70 hover:bg-gold/10">{st}</button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className={box}>
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Security Audit Log</p>
          <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
            {audit.map((a, i) => <p key={i} className="text-xs text-ivory/60"><span className="text-gold">{a.event}</span> · {a.actor_type}{a.actor_id ? `:${a.actor_id}` : ""} · {new Date(a.created_at).toLocaleString()}</p>)}
          </div>
        </div>
        <div className={box}>
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Email Delivery</p>
          <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
            {emails.length === 0 && <p className="text-xs text-ivory/50">No emails sent yet.</p>}
            {emails.map((e, i) => <p key={i} className="text-xs text-ivory/60"><span className={e.status === "failed" ? "text-red-400" : e.status === "sent" ? "text-gold-light" : "text-amber-400"}>{e.status}</span> · {e.kind} → {e.to}{e.error ? ` · ${e.error}` : ""}</p>)}
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------- Operations Board + View as Client (admin-only, read-only) ----------
const STATUS_OPTS = [
  ["confirmed", "Confirmed"], ["driver_assigned", "Driver Assigned"], ["driver_en_route", "Driver En Route"],
  ["driver_arrived", "Driver Arrived"], ["passenger_on_board", "Passenger On Board"], ["in_progress", "In Progress"],
  ["completed", "Completed"], ["canceled", "Canceled"],
];
const emptyFilters = { q: "", status: "", chauffeur: "", vehicle: "", date_from: "", date_to: "" };

function BoardStats({ s }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-5" data-testid="board-stats">
      <StatCard testid="board-stat-upcoming" label="Upcoming" value={s.upcoming} accent="#D4AF37" />
      <StatCard testid="board-stat-active" label="Active" value={s.active} accent="#34D399" />
      <StatCard testid="board-stat-completed" label="Completed" value={s.completed} accent="#10B981" />
      <StatCard testid="board-stat-canceled" label="Canceled" value={s.canceled} accent="#EF4444" />
      <StatCard testid="board-stat-total" label="Total" value={s.total} />
    </div>
  );
}

function BoardFilters({ filters, setFilters, data }) {
  const set = (k, v) => setFilters({ ...filters, [k]: v });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input data-testid="board-filter-search" placeholder="Search…" value={filters.q} onChange={(e) => set("q", e.target.value)} className={`${inp} w-44`} />
      <select data-testid="board-filter-status" value={filters.status} onChange={(e) => set("status", e.target.value)} className={inp}>
        <option value="">All statuses</option>
        {STATUS_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <select data-testid="board-filter-chauffeur" value={filters.chauffeur} onChange={(e) => set("chauffeur", e.target.value)} className={inp}>
        <option value="">All chauffeurs</option>
        {(data?.filters?.chauffeurs || []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select data-testid="board-filter-vehicle" value={filters.vehicle} onChange={(e) => set("vehicle", e.target.value)} className={inp}>
        <option value="">All vehicles</option>
        {(data?.filters?.vehicles || []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <DateField testid="board-filter-date-from" value={filters.date_from} onChange={(v) => set("date_from", v)} placeholder="From date" />
      <DateField testid="board-filter-date-to" value={filters.date_to} onChange={(v) => set("date_to", v)} placeholder="To date" />
    </div>
  );
}

function BoardTable({ data, loading, onOpen, showCompany }) {
  const items = data?.items || [];
  return (
    <div className="overflow-hidden rounded-2xl border border-gold/15 bg-obsidian-800" data-testid="board-table">
      <div className="hidden grid-cols-12 gap-2 border-b border-gold/10 px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-ivory/40 lg:grid">
        <span className="col-span-2">Confirmation</span>
        <span className="col-span-2">{showCompany ? "Company" : "Passenger"}</span>
        <span className="col-span-2">Pickup</span><span className="col-span-2">Route</span>
        <span>Chauffeur</span><span>Vehicle</span><span>Status</span><span></span>
      </div>
      {loading && <p className="px-5 py-10 text-center text-sm text-ivory/50" data-testid="board-loading">Loading…</p>}
      {!loading && items.length === 0 && <p className="px-5 py-10 text-center text-sm text-ivory/50" data-testid="board-empty">No reservations to display.</p>}
      {!loading && items.map((r) => (
        <button key={r.reservation_id} onClick={() => onOpen(r.reservation_id)} data-testid={`board-row-${r.confirmation_number}`} className="grid w-full grid-cols-1 gap-1 border-b border-gold/5 px-5 py-4 text-left text-sm transition hover:bg-gold/5 lg:grid-cols-12 lg:items-center lg:gap-2">
          <span className="col-span-2 font-mono text-gold">{r.confirmation_number}</span>
          {showCompany
            ? <span className="col-span-2 text-ivory/80">{r.company || <span className="text-ivory/40">Unassigned</span>}</span>
            : <span className="col-span-2 text-ivory">{r.passenger_name || "—"}</span>}
          <span className="col-span-2 text-ivory/70">{fmtDateTime(r.pickup_datetime)}</span>
          <span className="col-span-2 truncate text-ivory/60">{r.pickup_address} → {r.dropoff_address}</span>
          <span className="truncate text-ivory/70">{r.chauffeur_name || "—"}</span>
          <span className="truncate text-ivory/60">{r.vehicle || "—"}</span>
          <span><StatusBadge status={r.status} /></span>
          <span className="text-right text-xs text-gold/70">View</span>
        </button>
      ))}
    </div>
  );
}

function OperationsBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [detail, setDetail] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const { data } = await api.get(`/admin/portal/operations-board?${p.toString()}`);
      setData(data);
    } catch { toast.error("Could not load operations board"); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const open = async (id) => { try { const { data } = await api.get(`/admin/portal/operations-board/${id}`); setDetail(data); } catch { toast.error("Could not open reservation"); } };
  const s = data?.stats || { upcoming: 0, active: 0, completed: 0, canceled: 0, total: 0 };
  return (
    <div className="space-y-5" data-testid="ops-board">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold">Nanak Chauffeurs · Live Operations</p>
          <p className="mt-1 max-w-2xl text-xs text-ivory/50">Every accepted reservation you operate — across all corporate companies, individuals and drivers. Read-only; Limo Anywhere is the source of truth.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-ivory/45" data-testid="ops-last-sync">Last sync: {fmtDateTime(data?.last_success_at)}</span>
          <button onClick={load} data-testid="ops-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 px-3 py-1.5 text-xs text-ivory/80 hover:bg-gold/10"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
        </div>
      </div>
      <BoardStats s={s} />
      <BoardFilters filters={filters} setFilters={setFilters} data={data} />
      <BoardTable data={data} loading={loading} onOpen={open} showCompany />
      {data && data.total === 0 && !loading && (
        <p className="rounded-2xl border border-amber-500/25 bg-amber-500/5 px-5 py-3 text-xs text-amber-200/90" data-testid="ops-awaiting-sync">No live reservations yet. Both Zaps currently post to the secure /echo diagnostic endpoint; verified reservations appear here automatically once controlled production ingestion is approved.</p>
      )}
      {detail && <ReservationDetailDrawer r={detail} onClose={() => setDetail(null)} showCompany />}
    </div>
  );
}

function ViewAsClient() {
  const [companies] = useCompanies("approved");
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [detail, setDetail] = useState(null);
  const load = useCallback(async () => {
    if (!companyId) { setData(null); return; }
    setLoading(true);
    try {
      const p = new URLSearchParams({ company_id: companyId });
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const { data } = await api.get(`/admin/portal/view-as-client?${p.toString()}`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load client view"); }
    finally { setLoading(false); }
  }, [companyId, filters]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const open = async (id) => { try { const { data } = await api.get(`/admin/portal/view-as-client/${id}?company_id=${companyId}`); setDetail(data); } catch { toast.error("Could not open reservation"); } };
  const s = data?.stats || { upcoming: 0, active: 0, completed: 0, canceled: 0, total: 0 };
  const name = companies.find((c) => c.company_id === companyId)?.name;
  return (
    <div className="space-y-5" data-testid="view-as-client">
      <div className={box}>
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Preview a corporate client's view</p>
        <p className="mt-1 max-w-2xl text-xs text-ivory/50">See EXACTLY what an approved company's dispatchers would see — only accepted, matched reservations for that company. Unaccepted offers stay hidden and company isolation is fully preserved. No corporate user is impersonated and no fake account is created.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select data-testid="viewas-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className={inp}>
            <option value="">Select a corporate company…</option>
            {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
          {companyId && <button onClick={load} data-testid="viewas-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 px-3 py-1.5 text-xs text-ivory/80 hover:bg-gold/10"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh</button>}
        </div>
      </div>
      {!companyId && <p className="text-sm text-ivory/50" data-testid="viewas-prompt">Choose a company above to preview its reservation dashboard exactly as its dispatchers see it.</p>}
      {companyId && (
        <>
          <div className="rounded-xl border border-gold/20 bg-gold/5 px-4 py-2.5 text-xs text-gold" data-testid="viewas-banner">
            Previewing as <strong>{name}</strong> · read-only · exactly what their authorized dispatchers see
          </div>
          <BoardStats s={s} />
          <BoardFilters filters={filters} setFilters={setFilters} data={data} />
          <div className="space-y-3.5" data-testid="viewas-list">
            {loading && <p className="rounded-2xl border border-gold/12 bg-obsidian-800 px-5 py-10 text-center text-sm text-ivory/50">Loading…</p>}
            {!loading && (data?.items || []).map((r) => <ReservationListCard key={r.reservation_id} r={r} onOpen={open} />)}
          </div>
          {data && data.total === 0 && !loading && (
            <p className="rounded-2xl border border-amber-500/25 bg-amber-500/5 px-5 py-3 text-xs text-amber-200/90" data-testid="viewas-empty-note">No accepted reservations for {name} yet. Accepted, matched reservations appear here once controlled ingestion is live.</p>
          )}
        </>
      )}
      {detail && <ReservationDetailDrawer r={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
